import {
  createEffect,
  createSignal,
  type JSX,
  on,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { cn } from "@/libs/cn";

/**
 * La card che si stacca — promossa dal prototipo `linkedin-sync` (variante
 * "Vetrina"): a riposo è una riga nel flusso della pagina; al click viene
 * promossa a `position:fixed` esattamente sul rettangolo che occupava (un
 * segnaposto tiene il suo spazio) e da lì VIAGGIA verso il rettangolo espanso,
 * sopra uno scrim. Alla chiusura rifà il viaggio al contrario e ridiventa un
 * elemento del flusso: sempre la stessa card, mai due elementi (AC 35).
 *
 * L'altezza espansa non è una costante: la scrive un ResizeObserver sul
 * contenuto attivo, così i morph interni muovono la card stessa. La macchina
 * non aspetta MAI un'animazione: promozione e ritorno sono governati da
 * setTimeout, che non si congela quando il clock delle animazioni è fermo.
 *
 * Scrim e card vivono su un unico piano z-40 (D11), scrim prima nel DOM: non
 * 30 (la SaveBar z-30 resterebbe cliccabile sopra, bug AC 24), non 50 (il
 * LeaveDialog Kobalte a z-50 deve poter stare sopra, AC 25).
 */

type Mode = "rest" | "overlay" | "closing";

export type ExpandCardMotion = "spring" | "ease";

export type ExpandCardController = {
  /**
   * Chiusura senza viaggio (D16), per chi chiude la card perché la pagina
   * sotto è cambiata: il rettangolo di ritorno è stale, e un viaggio verso un
   * posto che si è mosso leggerebbe come un errore. Si ricompone sul posto,
   * subito. Il viewport che cambia larghezza non passa di qui: lo gestisce la
   * card da sé.
   */
  closeWithoutTravel: () => void;
};

export type ExpandCardProps = {
  open: boolean;
  /** Scrim ed Escape chiedono la chiusura: il chiamante decide se e come. */
  onDismiss: () => void;
  /** Dove viaggia la card: sopra il proprio posto, o al centro dello schermo. */
  placement: "in-place" | "center";
  expandedWidth: number;
  /**
   * La curva del viaggio, e con essa il raccoglimento che lo precede.
   *
   * `spring` (default) è il modello Dynamic Island: la card si raccoglie, poi
   * parte, e all'arrivo supera il rettangolo del 2,8% prima di posarcisi
   * sopra. Regge dove la card cambia soprattutto POSIZIONE — la card LinkedIn
   * è larga uguale da chiusa e da aperta, e lì il sorpasso si legge come peso
   * dell'oggetto.
   *
   * `ease` toglie entrambe le cose: nessuna anticipazione, nessun sorpasso,
   * la curva dei pannelli della casa (`--ease-drawer`, la stessa della
   * navigazione e delle sheet). Serve dove la card CRESCE molto: lì la molla
   * non fa sobbalzare la card, fa sobbalzare le sue DIMENSIONI, e si legge
   * come un ridimensionamento sbagliato invece che come un'apertura. Il
   * raccoglimento se ne va con lei — un'anticipazione seguita da una curva
   * che non rimbalza è un inciampo, non uno slancio.
   */
  motion?: ExpandCardMotion;
  /** Nome del dialogo per chi non lo vede (EC18). */
  label?: string;
  /** Aspetto della card (raggio, bordo, sfondo) in ogni stato. */
  class?: string;
  controller?: (api: ExpandCardController) => void;
  collapsed: () => JSX.Element;
  expanded: () => JSX.Element;
};

const MARGIN = 16;
/** Deve restare allineato a `--xcard-travel-dur` in `src/index.css`. */
const TRAVEL_MS = 380;
/**
 * La variante senza molla viaggia più corta. Non è solo gusto: la molla spende
 * la sua coda a posarsi sull'arrivo — sovraelongazione e rientro sono gli
 * ultimi ~80ms dei 380 — mentre `--ease-drawer` arriva e basta. A parità di
 * durata la seconda si legge quindi più lenta della prima, perché il tempo in
 * cui non succede più niente è tempo morto e non assestamento.
 */
const EASE_TRAVEL_MS = 300;
/**
 * I 40ms in reduced motion sono scheduling, non animazione: il commit del
 * rettangolo e il cleanup passano comunque da un giro di timer, e a 0ms il
 * ResizeObserver non avrebbe ancora misurato la faccia espansa (D13).
 */
const REDUCED_TRAVEL_MS = 40;
/**
 * Il raccoglimento che precede lo scatto (Dynamic Island): la card si stringe
 * di poco RESTANDO al suo posto, e solo quando è raccolta parte il viaggio.
 * Serve a togliere lo stacco netto della partenza — l'occhio aggancia la card
 * mentre è ancora ferma, poi la segue. La scala è quella del feedback di
 * pressione (0.95–0.98) e la curva è `ease-out`, non `ease-in`: anche una
 * anticipazione è una RISPOSTA al click e deve muoversi subito.
 *
 * La posa e le due durate stanno nel CSS (`data-crouch` in `src/index.css`):
 * qui resta solo il tempo, perché è la macchina a decidere QUANDO il viaggio
 * parte — cioè a raccoglimento finito. Sotto `prefers-reduced-motion` non
 * esiste: è l'anticipazione di un viaggio che lì non c'è (AC17).
 */
const CROUCH_MS = 110;

/**
 * Quanto restano spente le transizioni dopo che il viewport ha cambiato
 * larghezza (`data-refit`). Scheduling, non animazione: la larghezza nuova
 * arriva alle shell di `MorphView` al primo frame, e la faccia che le contiene
 * riporta la propria altezza solo al frame dopo (un ResizeObserver non
 * rinotifica nello stesso frame un antenato di chi ha appena notificato).
 *
 * Misurato in Chromium (2026-09-18, CPU rallentata 6×, rotazioni 1180↔820 e
 * 1180↔390): l'ultima altezza arriva entro 7ms dal `resize`, e in 1,5s
 * nessuna transizione di geometria parte, né sulla card né sulle shell —
 * nemmeno quando l'attributo se ne va. I 100ms sono il margine, non la misura.
 */
const REFIT_SETTLE_MS = 100;

/**
 * Quanto dura il viaggio. Esposto perché chi in pagina deve aspettare la card
 * ferma — il reload del form dopo un sync riuscito — non può tenere una
 * costante propria: sotto `prefers-reduced-motion` la card si posa dieci volte
 * prima, e un ritardo scritto a mano lascerebbe una finestra in cui la card è
 * di nuovo cliccabile mentre il remount deve ancora arrivare.
 */
export const expandTravelMs = (
  prefersReduced: boolean,
  motion: ExpandCardMotion = "spring",
) => {
  if (prefersReduced) {
    return REDUCED_TRAVEL_MS;
  }
  return motion === "ease" ? EASE_TRAVEL_MS : TRAVEL_MS;
};

/**
 * Chi è esente dal trap, per due ragioni diverse:
 *
 * 1. ha un proprio writer di `inert` — la SaveBar (D12): due writer sullo
 *    stesso attributo si cancellano a vicenda;
 * 2. deve restare nell'accessibility tree mentre l'overlay è aperto — le
 *    live region. `inert` non nasconde soltanto: toglie l'elemento
 *    dall'albero, e una regione `aria-live` tolta dall'albero non annuncia
 *    più niente proprio mentre c'è qualcosa da annunciare.
 *
 * In entrambi i casi l'elemento non porta tab stop, quindi esentarlo non apre
 * un buco nel confinamento.
 */
const TRAP_EXEMPT_SELECTOR = "[data-overlay-exempt]";

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(min, max));

export const ExpandCard = (props: ExpandCardProps) => {
  const prefersReduced = usePrefersReducedMotion();
  const [mode, setMode] = createSignal<Mode>("rest");
  const travel = () => expandTravelMs(prefersReduced(), motion());
  const motion = () => props.motion ?? "spring";

  let holder: HTMLDivElement | undefined;
  let card: HTMLDivElement | undefined;
  let observer: ResizeObserver | undefined;
  let closeTimer: ReturnType<typeof setTimeout> | undefined;
  let arrivedTimer: ReturnType<typeof setTimeout> | undefined;
  let crouchTimer: ReturnType<typeof setTimeout> | undefined;
  let refitTimer: ReturnType<typeof setTimeout> | undefined;

  /*
    La larghezza del viewport su cui la card ha fatto i conti. Si legge DOPO
    `lockScroll()`: su un desktop con barre classiche il blocco toglie la barra
    di scorrimento, e una misura presa prima farebbe sembrare il primo `resize`
    qualunque un cambio di larghezza.
  */
  let viewportWidth = 0;
  /** Il posto a cui tornare si è mosso: la chiusura non viaggia (D16). */
  let stale = false;

  /*
    Il lock compensa la scrollbar che sparisce: senza il padding la pagina si
    sposterebbe DOPO la misura del rettangolo di partenza, e la card
    tornerebbe a un posto che nel frattempo si è mosso di qualche pixel.
    Owner unico dello scroll lock: nessuna utility condivisa nel repo (A5).
  */
  const lockScroll = () => {
    const root = document.documentElement;
    const scrollbar = window.innerWidth - root.clientWidth;
    root.style.overflow = "hidden";
    if (scrollbar > 0) root.style.paddingRight = `${scrollbar}px`;
  };
  const unlockScroll = () => {
    const root = document.documentElement;
    root.style.overflow = "";
    root.style.paddingRight = "";
  };

  /*
    Il trap (EC18/D12): `inert` sui fratelli di ogni antenato fino a `body`.
    L'insieme che governa si legge una volta sola, all'apertura. Ma su quegli
    stessi elementi scrive anche la pagina: il drawer di `Navigation.tsx` mette
    e toglie il proprio `inert` quando il viewport attraversa `lg`, cioè a ogni
    rotazione di un iPad a card aperta. Un trap che ricordasse soltanto che
    cosa ha preso restituirebbe alla chiusura un drawer nascosto senza `inert`,
    e lascerebbe raggiungibile dentro il dialogo una barra laterale appena
    diventata permanente.

    Quindi il trap ricorda che cosa ne vuole LA PAGINA, e lo aggiorna a ogni
    scrittura altrui finché la card è aperta: alla chiusura ogni elemento
    torna come la pagina lo vuole in quel momento, non com'era all'apertura.
    Non dipende dall'ordine in cui `resize` e il `change` di `matchMedia`
    arrivano, perché non ascolta il viewport: ascolta l'attributo.

    Il contratto con gli altri scrittori: ogni loro scrittura deve produrre un
    record di mutazione. La proprietà (`inert={…}` di Solid compila in
    `el.inert = …`) e `setAttribute`/`removeAttribute` lo producono sempre,
    anche a valore invariato. `toggleAttribute("inert", true)` su un attributo
    già presente, o una lettura prima della scrittura (`if (!el.inert) …`),
    NO: sull'elemento che il trap tiene la pagina vedrebbe `inert` già messo,
    non scriverebbe niente, e alla chiusura il trap glielo toglierebbe.

    Resta un limite di prima: l'insieme non cresce. Un fratello montato DOPO
    l'apertura non è governato — ed è ciò che lascia usabile un dialogo in
    portale sopra la card (il LeaveDialog durante un volo, AC 25).
  */
  let governed = new Map<Element, boolean>();
  let pageWrites: MutationObserver | undefined;
  let returnFocus: HTMLElement | undefined;

  /** Le scritture altrui: il trap le legge, non le subisce. */
  const learn = (records: MutationRecord[]) => {
    for (const record of records) {
      const element = record.target as Element;
      if (governed.has(element)) {
        governed.set(element, element.hasAttribute("inert"));
      }
    }
  };

  /**
   * Le scritture del trap. I record che producono si scartano subito, senza
   * cedere il controllo: sono i nostri, e non devono mai arrivare a `learn`
   * come volontà della pagina. I due chiamanti non lasciano record altrui in
   * coda: `trapFocus` ha appena creato l'observer, la callback li ha appena
   * consegnati.
   */
  const hold = () => {
    if (!pageWrites) return;
    for (const element of governed.keys()) {
      if (!element.hasAttribute("inert")) element.setAttribute("inert", "");
    }
    pageWrites.takeRecords();
  };

  const trapFocus = () => {
    if (!holder) return;
    const active = document.activeElement;
    returnFocus = active instanceof HTMLElement ? active : undefined;
    governed = new Map();
    let node: Element = holder;
    while (node.parentElement) {
      const parent: Element = node.parentElement;
      for (const sibling of Array.from(parent.children)) {
        if (sibling === node) continue;
        if (sibling.matches(TRAP_EXEMPT_SELECTOR)) continue;
        governed.set(sibling, sibling.hasAttribute("inert"));
      }
      if (parent === document.body) break;
      node = parent;
    }
    pageWrites = new MutationObserver((records) => {
      learn(records);
      hold();
    });
    for (const element of governed.keys()) {
      pageWrites.observe(element, { attributeFilter: ["inert"] });
    }
    hold();
  };

  /** Restituisce gli `inert` senza toccare il fuoco: serve anche allo smontaggio. */
  const releaseInert = () => {
    if (pageWrites) {
      learn(pageWrites.takeRecords());
      pageWrites.disconnect();
      pageWrites = undefined;
    }
    for (const [element, pageWantsInert] of governed) {
      if (!pageWantsInert) element.removeAttribute("inert");
    }
    governed = new Map();
  };

  const releaseFocus = () => {
    releaseInert();
    if (returnFocus?.isConnected) {
      returnFocus.focus({ preventScroll: true });
    }
    returnFocus = undefined;
  };

  const targetGeometry = () => {
    const vw = document.documentElement.clientWidth;
    const width = Math.min(props.expandedWidth, vw - MARGIN * 2);
    if (!holder) return { width, left: MARGIN };
    const rect = holder.getBoundingClientRect();
    const left =
      props.placement === "center"
        ? (vw - width) / 2
        : clamp(
            rect.left + rect.width / 2 - width / 2,
            MARGIN,
            vw - width - MARGIN,
          );
    return { width, left };
  };

  /**
   * Altezza — e top, che ne dipende — seguono il contenuto attivo, con il
   * tetto del viewport (D15): oltre `100dvh - 32px` scorre il contenuto della
   * faccia, mai la shell. Sotto 768px la card si ancora a 16px dall'alto,
   * fuori dalla zona che la tastiera contende (EC16).
   */
  const applyHeight = (contentHeight: number) => {
    if (!card || !holder) return;
    const vh = document.documentElement.clientHeight;
    const height = Math.min(contentHeight, vh - MARGIN * 2);
    card.style.height = `${height}px`;
    const isNarrow = document.documentElement.clientWidth < 768;
    const top = isNarrow
      ? // Anche centrata: sotto 768px il centro verticale è esattamente dove
        // arriva la tastiera, e il campo URL con le sue conferme finirebbe
        // sotto di essa (D15/EC16). Ancorata in alto ha tutto lo spazio.
        MARGIN
      : props.placement === "center"
        ? Math.max(MARGIN, (vh - height) / 2)
        : clamp(
            holder.getBoundingClientRect().top - 12,
            MARGIN,
            vh - height - MARGIN,
          );
    card.style.top = `${top}px`;
  };

  /*
    Chiudere a metà anticipazione è un caso reale (Escape arriva in 110ms):
    senza togliere l'attributo la card tornerebbe al suo posto rimpicciolita.
  */
  const dropCrouch = () => {
    clearTimeout(crouchTimer);
    card?.removeAttribute("data-crouch");
  };

  const promote = () => {
    if (!card || !holder || mode() !== "rest") return;
    lockScroll();
    viewportWidth = document.documentElement.clientWidth;
    trapFocus();
    const rect = card.getBoundingClientRect();
    /*
      Il segnaposto tiene ENTRAMBE le misure. L'altezza è sempre servita; la
      larghezza serve da quando una faccia a riposo può essere larga quanto il
      proprio contenuto invece che quanto il contenitore (la card del link
      pubblico): staccata la card, il posto si richiuderebbe a larghezza zero e
      `retire()` — che rimisura l'holder per sapere dove tornare — riporterebbe
      la card dentro un rettangolo collassato. Dove la riga era già a piena
      larghezza questo non cambia niente: riscrive la misura che aveva.
    */
    holder.style.height = `${rect.height}px`;
    holder.style.width = `${rect.width}px`;
    const style = card.style;
    style.position = "fixed";
    style.top = `${rect.top}px`;
    style.left = `${rect.left}px`;
    style.width = `${rect.width}px`;
    style.height = `${rect.height}px`;
    style.maxWidth = "none";
    style.margin = "0";
    style.zIndex = "40";
    setMode("overlay");
    // Commit del rettangolo di partenza: la promozione stessa non anima,
    // il viaggio parte da qui.
    card.getBoundingClientRect();
    card.setAttribute("data-anim", "");

    /*
      Il viaggio vero e proprio: lo scrive `launch()`, che parte subito quando
      il raccoglimento non c'è (reduced motion) e dopo CROUCH_MS quando c'è.
      L'altezza (e il top) li scrive il ResizeObserver appena la faccia
      espansa ha misurato. Dopo l'arrivo, i morph interni viaggiano al passo
      della SaveBar (vedi data-arrived nel CSS).
    */
    const launch = () => {
      if (!card || mode() !== "overlay") return;
      const { width, left } = targetGeometry();
      card.style.width = `${width}px`;
      card.style.left = `${left}px`;
      clearTimeout(arrivedTimer);
      arrivedTimer = setTimeout(
        () => card?.setAttribute("data-arrived", ""),
        travel() + 20,
      );
    };

    /*
      Due strade che partono subito, per ragioni diverse: sotto reduced motion
      perché AC17 vieta il viaggio, e in variante `ease` perché il
      raccoglimento è l'anticipazione di una molla che lì non c'è.
    */
    clearTimeout(crouchTimer);
    if (prefersReduced() || motion() === "ease") {
      launch();
      return;
    }

    /*
      Il rilascio della scala e la crescita del rettangolo partono nello stesso
      frame, così per l'occhio sono una cosa sola: la card si raccoglie, poi si
      espande fino a completare il morph. Entrambi ridono la stessa molla
      (`--ease-morph-spring`), quindi arrivano insieme.
    */
    card.setAttribute("data-crouch", "");
    crouchTimer = setTimeout(() => {
      dropCrouch();
      launch();
    }, CROUCH_MS);
  };

  /**
   * @param resume - se la card è stata riaperta durante il viaggio di ritorno,
   *   rilancia la promozione. Falso solo per `closeWithoutTravel()` (D16), dove
   *   il chiamante chiude subito dopo e un ri-promuovere qui aggiungerebbe
   *   esattamente il viaggio che quella strada esiste per non fare.
   */
  const settle = (resume = true) => {
    if (!card || !holder) return;
    dropCrouch();
    clearTimeout(refitTimer);
    stale = false;
    card.removeAttribute("data-refit");
    card.removeAttribute("data-anim");
    card.style.cssText = "";
    holder.style.height = "";
    holder.style.width = "";
    unlockScroll();
    setMode("rest");
    /*
      L'ordine conta, e non per eleganza: `releaseFocus()` deve trovare la
      faccia collassata già raggiungibile. Su `closeWithoutTravel()` si entra
      qui col modo ancora "overlay" — la faccia è `inert`, e un `focus()` dentro
      un sottoalbero inert è un no-op silenzioso: il focus resterebbe su
      `<body>`, fuori da tutto. L'attributo si toglie a mano invece di
      aspettare l'effetto sul modo, che sotto un `createEffect` chiamante non ha
      ancora girato; l'effetto poi riscriverà lo stesso valore.
    */
    collapsedFace?.removeAttribute("inert");
    releaseFocus();
    /*
      Riaperta mentre tornava. Nei ~400ms del ritorno `promote()` usciva subito
      (il modo non era "rest") e la macchina restava «aperta» sopra una card a
      riposo: niente scrim, niente Escape, la riga che non riapre e la SaveBar
      `inert` fino al reload. Il mouse è fuori gioco per `pointer-events:none`,
      la tastiera no — chiusa la card il focus è sulla riga, e un Invio ci
      arriva in un attimo.
    */
    if (resume && props.open) promote();
  };

  const retire = () => {
    if (!card || !holder || mode() !== "overlay") return;
    if (stale) {
      closeWithoutTravel();
      return;
    }
    setMode("closing");
    observer?.disconnect();
    dropCrouch();
    clearTimeout(arrivedTimer);
    card.removeAttribute("data-arrived");
    const rect = holder.getBoundingClientRect();
    const style = card.style;
    style.top = `${rect.top}px`;
    style.left = `${rect.left}px`;
    style.width = `${rect.width}px`;
    style.height = `${rect.height}px`;
    clearTimeout(closeTimer);
    closeTimer = setTimeout(() => settle(), travel() + 20);
  };

  const closeWithoutTravel = () => {
    if (!card || !holder || mode() === "rest") return;
    observer?.disconnect();
    dropCrouch();
    clearTimeout(arrivedTimer);
    clearTimeout(closeTimer);
    card.removeAttribute("data-arrived");
    settle(false);
  };

  /**
   * Il viewport si ricompone in un frame, e la card con lui: senza
   * transizioni, né della card né delle shell che morfano al suo interno.
   * Non è un gesto dell'utente da accompagnare: una card che scivola verso il
   * posto nuovo quando la pagina è già lì si legge come un ritardo.
   * `data-refit` le spegne dal CSS finché le altezze nuove non sono arrivate.
   *
   * Durante il raccoglimento non c'è niente da rimisurare: il viaggio non è
   * ancora partito, e `launch()` leggerà il viewport nuovo.
   */
  const refit = () => {
    if (!card || card.hasAttribute("data-crouch")) return;
    card.setAttribute("data-refit", "");
    const { width, left } = targetGeometry();
    card.style.width = `${width}px`;
    card.style.left = `${left}px`;
    if (expandedFace) applyHeight(expandedFace.offsetHeight);
    clearTimeout(refitTimer);
    refitTimer = setTimeout(
      () => card?.removeAttribute("data-refit"),
      REFIT_SETTLE_MS,
    );
  };

  /**
   * Il viewport cambia larghezza a card aperta: rotazione, finestra
   * ridimensionata, zoom. La card resta aperta e si rimisura. Chiuderla non è
   * sempre permesso — la sincronizzazione LinkedIn in volo (AC 24) e il
   * salvataggio dello username (EC9) la vogliono aperta — e il link pubblico,
   * riaprendo, azzera la bozza che il freelance stava scrivendo. Il posto da
   * cui è partita invece si è mosso: da qui in poi la chiusura è senza
   * viaggio.
   *
   * Solo la LARGHEZZA: la tastiera virtuale e la barra dell'URL cambiano
   * l'altezza, e lì la card resta ferma (D15).
   */
  const onViewportResize = () => {
    if (mode() === "rest") return;
    const width = document.documentElement.clientWidth;
    if (width === viewportWidth) return;
    viewportWidth = width;
    if (mode() === "closing") {
      // Stava tornando verso un posto che la pagina ha appena spostato.
      clearTimeout(closeTimer);
      settle();
      return;
    }
    stale = true;
    refit();
  };

  props.controller?.({ closeWithoutTravel });

  createEffect(
    on(
      () => props.open,
      (open) => (open ? promote() : retire()),
      {
        defer: true,
      },
    ),
  );

  onMount(() => {
    /*
      L'effetto qui sopra è `defer`, quindi al mount non dice niente — ed è
      giusto nel caso normale, dove `open` nasce falso. Ma questo componente
      può essere rimontato SOTTO una card già aperta (il sottoalbero della
      pagina è `keyed`: basta un remount del form). Senza questa riga la
      macchina resterebbe «aperta» sopra una card a riposo: niente scrim,
      niente Escape, la riga che non riapre — e la SaveBar `inert` fino al
      reload della pagina, perché nessuno le restituirebbe i suoi poteri.
    */
    if (props.open) promote();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && mode() === "overlay") props.onDismiss();
    };
    document.addEventListener("keydown", onKey);
    onCleanup(() => document.removeEventListener("keydown", onKey));
    window.addEventListener("resize", onViewportResize);
    onCleanup(() => window.removeEventListener("resize", onViewportResize));
  });

  onCleanup(() => {
    clearTimeout(closeTimer);
    clearTimeout(arrivedTimer);
    clearTimeout(crouchTimer);
    clearTimeout(refitTimer);
    observer?.disconnect();
    // Smontata mentre era aperta (cambio workspace): la pagina deve riavere
    // scroll e interattività.
    if (mode() !== "rest") {
      releaseInert();
      unlockScroll();
    }
  });

  const observeExpanded = (el: HTMLDivElement) => {
    observer?.disconnect();
    observer = new ResizeObserver(() => {
      if (mode() === "overlay") applyHeight(el.offsetHeight);
    });
    observer.observe(el);
  };

  let collapsedFace: HTMLDivElement | undefined;
  let expandedFace: HTMLDivElement | undefined;
  // La faccia nascosta è solo un'immagine di sé: niente tab stop, niente
  // click fantasma.
  createEffect(() =>
    collapsedFace?.toggleAttribute("inert", mode() === "overlay"),
  );
  createEffect(() =>
    expandedFace?.toggleAttribute("inert", mode() === "closing"),
  );

  /**
   * EC18, «il focus entra».
   *
   * Non lo garantiva niente: la card si dichiara `aria-modal`, ma dentro il
   * focus lo portava solo `autofocusSoft`, che vive sulle sole facce col campo
   * URL ed esce comunque su puntatore grosso o sotto i 1024px. Sull'hub — il
   * caso normale dell'AC 12 — e su qualunque telefono, il focus restava sulla
   * faccia collassata, che l'apertura rende `inert`: finiva su `<body>`, fuori
   * dal dialogo che dichiara di contenerlo.
   *
   * Un effetto e non il `ref`: in Solid il `ref` gira PRIMA che il nodo sia nel
   * documento, e `focus()` su un nodo staccato non fa niente. Gli effetti
   * girano dopo il render, che è l'unico momento in cui la domanda ha senso.
   *
   * `autofocusSoft` resta com'è: da raffinamento desktop sopra una garanzia
   * che adesso esiste, invece di essere l'unica cosa che la teneva.
   */
  createEffect(
    on(mode, (current) => {
      if (current !== "overlay") return;
      expandedFace?.focus({ preventScroll: true });
    }),
  );

  return (
    <div ref={holder} class="xcard-holder">
      <Show when={mode() !== "rest"}>
        <div
          class="xcard-scrim"
          data-on={mode() === "overlay" ? "" : undefined}
          onClick={() => props.onDismiss()}
          aria-hidden="true"
        />
      </Show>
      <div
        ref={card}
        class={cn("xcard", props.class)}
        data-mode={mode()}
        data-motion={motion()}
      >
        <div
          ref={collapsedFace}
          class="xcard-face"
          data-off={mode() === "overlay" ? "" : undefined}
        >
          {props.collapsed()}
        </div>
        <Show when={mode() !== "rest"}>
          <div
            ref={(el) => {
              expandedFace = el;
              observeExpanded(el);
            }}
            class="xcard-face-expanded"
            data-off={mode() === "closing" ? "" : undefined}
            role="dialog"
            aria-modal="true"
            aria-label={props.label}
            /* Focusable da codice, mai in tabulazione: è il contenitore. */
            tabindex="-1"
          >
            {props.expanded()}
          </div>
        </Show>
      </div>
    </div>
  );
};
